import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../components/common/DataTable";
import BlockNoteEditor from "../components/common/BlockNoteEditor";
import { useAdminApi } from "../hooks/useAdminApi";
import { HelpArticle, HelpCategory, Paginated } from "../types/admin";
import { toast } from "react-toastify";

const emptyArticle: Partial<HelpArticle> = {
	title: "",
	body_markdown: "",
	status: "draft",
	category_id: 0,
	locale: "ja-JP",
};

const HelpPage: React.FC = () => {
	const api = useAdminApi();
	const [categories, setCategories] = useState<HelpCategory[]>([]);
	const [articles, setArticles] = useState<Paginated<HelpArticle> | null>(null);
	const [filters, setFilters] = useState({ status: "", category_id: "" });
	const [editing, setEditing] = useState<Partial<HelpArticle>>(emptyArticle);
	const [detailLoading, setDetailLoading] = useState(false);
	const [categoryForm, setCategoryForm] = useState<{
		name: string;
		parent_id: string;
		locale: string;
	}>({
		name: "",
		parent_id: "",
		locale: "ja-JP",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadCategories = async () => {
		try {
			const res = await api.listHelpCategories({ locale: "ja-JP" });
			setCategories(res);
			if (!editing.category_id && res.length) {
				setEditing((prev) => ({ ...prev, category_id: res[0].id }));
			}
		} catch (err) {
			console.error(err);
			toast.error("カテゴリの取得に失敗しました");
		}
	};

	const loadArticles = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.listHelpArticles({
				status: filters.status || undefined,
				category_id: filters.category_id || undefined,
			});
			setArticles(res);
		} catch (err) {
			console.error(err);
			setError("ヘルプ記事の取得に失敗しました");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadCategories();
		loadArticles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const columns = useMemo(
		() => [
			{ key: "id", header: "ID" },
			{ key: "title", header: "タイトル" },
			{ key: "status", header: "状態" },
			{ key: "category_id", header: "カテゴリ" },
		],
		[]
	);

	const saveCategory = async () => {
		try {
			await api.createHelpCategory({
				name: categoryForm.name,
				parent_id: categoryForm.parent_id
					? Number(categoryForm.parent_id)
					: undefined,
				locale: categoryForm.locale,
			});
			toast.success("カテゴリを作成しました");
			setCategoryForm({ name: "", parent_id: "", locale: "ja-JP" });
			loadCategories();
		} catch (err) {
			console.error(err);
			toast.error("カテゴリ作成に失敗しました");
		}
	};

	const startEdit = async (article?: HelpArticle) => {
		if (!article?.id) {
			setEditing({ ...emptyArticle, category_id: categories[0]?.id });
			return;
		}

		setDetailLoading(true);
		try {
			const full = await api.getHelpArticle(article.id);
			setEditing(full);
		} catch (err) {
			console.error(err);
			toast.error("記事詳細の取得に失敗しました");
			setEditing({ ...article });
		} finally {
			setDetailLoading(false);
		}
	};

	const saveArticle = async () => {
		if (!editing.title || !editing.body_markdown || !editing.category_id) {
			toast.error("タイトル・本文・カテゴリは必須です");
			return;
		}
		const payload = {
			...editing,
			category_id: Number(editing.category_id),
		} as Partial<HelpArticle>;
		try {
			if (editing.id) {
				await api.updateHelpArticle(editing.id, payload);
			} else {
				const res = await api.createHelpArticle(payload);
				setEditing(res.data?.data ?? res.data);
			}
			toast.success("保存しました");
			loadArticles();
		} catch (err) {
			console.error(err);
			toast.error("保存に失敗しました");
		}
	};

	const publishArticle = async () => {
		if (!editing.id) return;
		try {
			await api.publishHelpArticle(editing.id);
			toast.success("公開しました");
			loadArticles();
		} catch (err) {
			console.error(err);
			toast.error("公開に失敗しました");
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-ink">Help Center</h1>
					<p className="text-sm text-muted">
						カテゴリ管理と Markdown でのヘルプ記事執筆
					</p>
				</div>
				<button
					className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
					onClick={() => startEdit()}
				>
					新規記事
				</button>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card space-y-3">
				<h3 className="text-lg font-semibold text-ink">カテゴリ</h3>
				<div className="grid gap-2 md:grid-cols-4">
					<input
						className="input"
						placeholder="カテゴリ名"
						value={categoryForm.name}
						onChange={(e) =>
							setCategoryForm({ ...categoryForm, name: e.target.value })
						}
					/>
					<select
						className="input"
						value={categoryForm.parent_id}
						onChange={(e) =>
							setCategoryForm({ ...categoryForm, parent_id: e.target.value })
						}
					>
						<option value="">親カテゴリなし</option>
						{categories.map((c) => (
							<option key={c.id} value={c.id}>
								{c.name}
							</option>
						))}
					</select>
					<input
						className="input"
						placeholder="locale"
						value={categoryForm.locale}
						onChange={(e) =>
							setCategoryForm({ ...categoryForm, locale: e.target.value })
						}
					/>
					<button
						className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
						onClick={saveCategory}
					>
						追加
					</button>
				</div>
				<div className="flex flex-wrap gap-2 text-xs">
					{categories.map((c) => (
						<span key={c.id} className="pill">
							{c.name}
						</span>
					))}
				</div>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-2 md:grid-cols-3">
					<select
						className="input"
						value={filters.status}
						onChange={(e) => setFilters({ ...filters, status: e.target.value })}
					>
						<option value="">状態</option>
						<option value="draft">draft</option>
						<option value="published">published</option>
					</select>
					<select
						className="input"
						value={filters.category_id}
						onChange={(e) =>
							setFilters({ ...filters, category_id: e.target.value })
						}
					>
						<option value="">カテゴリ</option>
						{categories.map((c) => (
							<option key={c.id} value={c.id}>
								{c.name}
							</option>
						))}
					</select>
					<button
						className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
						onClick={loadArticles}
					>
						絞り込み
					</button>
				</div>

				<div className="mt-4">
					<DataTable
						data={articles?.data ?? []}
						columns={columns}
						total={articles?.total ?? 0}
						loading={loading}
						error={error}
						onRowClick={(row) => startEdit(row as HelpArticle)}
					/>
				</div>
			</div>

			{editing && (
				<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card space-y-3">
					{detailLoading && (
						<p className="text-sm text-muted">本文を読み込み中…</p>
					)}
					<div className="grid gap-3 md:grid-cols-2">
						<label className="form-field">
							タイトル
							<input
								className="input"
								value={editing.title}
								onChange={(e) =>
									setEditing({ ...editing, title: e.target.value })
								}
							/>
						</label>
						<label className="form-field">
							スラッグ
							<input
								className="input"
								value={editing.slug ?? ""}
								onChange={(e) =>
									setEditing({ ...editing, slug: e.target.value })
								}
							/>
						</label>
						<label className="form-field">
							カテゴリ
							<select
								className="input"
								value={editing.category_id ?? ""}
								onChange={(e) =>
									setEditing({
										...editing,
										category_id: Number(e.target.value),
									})
								}
							>
								{categories.map((c) => (
									<option key={c.id} value={c.id}>
										{c.name}
									</option>
								))}
							</select>
						</label>
						<label className="form-field">
							ロケール
							<input
								className="input"
								value={editing.locale ?? ""}
								onChange={(e) =>
									setEditing({ ...editing, locale: e.target.value })
								}
							/>
						</label>
					</div>

					<BlockNoteEditor
						value={editing.body_markdown ?? ""}
						onChange={(val) => setEditing({ ...editing, body_markdown: val })}
						minHeight="360px"
					/>

					<div className="flex gap-2 justify-end">
						<button className="pill" onClick={saveArticle}>
							保存
						</button>
						{editing.id && (
							<button
								className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
								onClick={publishArticle}
							>
								公開する
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default HelpPage;
